"use client";

import clsx from "clsx";
import { useEffect } from "react";
import { useAuth } from "@repo/ui/auth";
import { useStore } from "@repo/ui/store";
import {
  C2C_LINK_SRC,
  C2C_SCRIPT_SRC,
  ORIGINS,
  SDK_LINK_SRC,
  SDK_SCRIPT_SRC,
} from "../../constants";
import { initialise } from "../../helpers/initialise";
import { useLinkTag } from "../../hooks/use-link-tag";
import { useScriptTag } from "../../hooks/use-script-tag";
import { IdentityErrorMessage } from "../identity-error-message/identity-error-message";
import { NuanceErrorMessage } from "../nuance-error-message/nuance-error-message";
import styles from "./chatbot.module.css";

export const Chatbot = ({
  status,
  onStatusChange,
}: {
  status: string;
  onStatusChange: (state: string) => void;
}) => {
  const { status: authStatus, authClient } = useAuth();
  const { countryData, shop } = useStore();

  const isActive = status === "chatactive";

  const sdkScriptTagStatus = useScriptTag(SDK_SCRIPT_SRC);
  const sdkLinkTagStatus = useLinkTag(SDK_LINK_SRC);
  const c2cScriptStatus = useScriptTag(C2C_SCRIPT_SRC);
  const c2cLinkStatus = useLinkTag(C2C_LINK_SRC);
  const assetsLoaded = [
    sdkScriptTagStatus,
    sdkLinkTagStatus,
    c2cScriptStatus,
    c2cLinkStatus,
  ].every((value) => value === "ready");

  const onMessage = async (event: MessageEvent) => {
    if (!ORIGINS.includes(event.origin)) return;

    const data = JSON.parse(event.data);
    const loginTrigger = data?.trigger === "AsosLogin";

    if (!loginTrigger) return;

    authClient?.redirectToLogin();
  };

  useEffect(() => {
    async function init() {
      if (authStatus === "initialising") return;

      if (assetsLoaded) {
        try {
          const customerId =
            authStatus === "authenticated"
              ? authClient.getIdTokenClaims().customerId
              : (authClient.getCustomerAnonymousId() ?? "");

          const accessToken =
            authStatus === "authenticated"
              ? ((await authClient.getAccessToken()) ?? "")
              : ""; // TODO
          const isAuthenticated = authStatus === "authenticated";

          const nuanceData = {
            browseCountry: countryData.countryCode,
            browseLanguage: countryData.defaultLanguage,
            customerId,
            isAuthenticated,
            referrer: document.referrer,
            storeCode: countryData.storeCode,
            shop,
            accessToken,
            firstName: isAuthenticated
              ? authClient.getIdTokenClaims().firstName
              : undefined,
            lastName: isAuthenticated
              ? authClient.getIdTokenClaims().lastName
              : undefined,
          };

          await initialise(nuanceData, onStatusChange);
        } catch (error) {
          if (error instanceof Error) {
            const errorType = error.message.toLowerCase().replaceAll(" ", "-");
            onStatusChange(errorType);
          }
        }
      }
    }

    init();

    window.addEventListener("message", onMessage, false);

    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [assetsLoaded, authStatus]);

  if (status === "identity-timeout") {
    return <IdentityErrorMessage />;
  }

  if (status === "nuance-timeout") {
    return <NuanceErrorMessage />;
  }

  return (
    <>
      <div
        id="nuance-chat-fixed"
        data-testid="chat-bot"
        className={clsx({
          [styles.hidden!]: isActive,
        })}
      />
      <div
        id="nuanMessagingFrame"
        className={clsx(styles.chatFrame, {
          [styles.hidden!]: !isActive,
        })}
      />
    </>
  );
};
